import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Banknote,
  Building,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  DollarSign,
  Loader2,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

import { API } from "./utils";


function TransactionsTab() {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local input state for debouncing
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 50;
  
  // Manual transaction form
  const [manualTx, setManualTx] = useState({
    amount: '',
    description: '',
    category: 'Other',
    payment_method: 'check',
    customer_name: '',
    customer_email: '',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  // Format time to EST
  const formatToEST = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' EST';
    } catch (e) {
      return null;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const fetchTransactions = async (showLoader = true, page = 0, search = '') => {
    if (showLoader) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'All') params.append('category', categoryFilter);
      if (search) params.append('search', search);
      params.append('limit', PAGE_SIZE.toString());
      params.append('offset', (page * PAGE_SIZE).toString());
      
      const response = await axios.get(`${API}/api/transactions?${params.toString()}`);
      setTransactions(response.data.transactions || []);
      setCategoryStats(response.data.category_stats || []);
      setTotalCount(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      setCurrentPage(page);
    } catch (err) {
      toast.error("Failed to load transactions");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/transactions/categories`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error("Failed to load categories");
    }
  };

  const fetchLastSyncTime = async () => {
    try {
      const response = await axios.get(`${API}/api/transactions/last-sync`);
      if (response.data.last_sync) {
        setLastSyncTime(response.data.last_sync);
      }
    } catch (err) {
      console.error("Failed to fetch last sync time");
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/transactions/summary`);
      setSummary(response.data);
    } catch (err) {
      console.error("Failed to load summary");
    }
  };

  const syncStripeTransactions = async (fullSync = false) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/api/transactions/sync-stripe?full_sync=${fullSync}`);
      toast.success(`Synced ${response.data.synced} new transaction(s)`);
      if (response.data.last_sync) {
        setLastSyncTime(response.data.last_sync);
      }
      fetchTransactions();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync transactions");
    } finally {
      setSyncing(false);
    }
  };

  const createManualTransaction = async () => {
    if (!manualTx.amount || !manualTx.description) {
      toast.error("Amount and description are required");
      return;
    }
    
    try {
      await axios.post(`${API}/api/transactions/manual`, manualTx);
      toast.success("Transaction created");
      setShowCreateDialog(false);
      setManualTx({
        amount: '',
        description: '',
        category: 'Other',
        payment_method: 'check',
        customer_name: '',
        customer_email: '',
        transaction_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchTransactions(true, 0, searchQuery);
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create transaction");
    }
  };

  const updateTransactionCategory = async (id, category) => {
    try {
      await axios.patch(`${API}/api/transactions/${id}`, { category });
      toast.success("Category updated");
      fetchTransactions(false, currentPage, searchQuery); // Don't show loader - prevents scroll jump
      fetchSummary();
    } catch (err) {
      toast.error("Failed to update category");
    }
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await axios.delete(`${API}/api/transactions/${id}`);
      toast.success("Transaction deleted");
      fetchTransactions(true, currentPage, searchQuery);
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete transaction");
    }
  };

  const createCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API}/api/transactions/categories`, { name: newCategory.trim() });
      toast.success("Category created");
      setNewCategory('');
      setShowCategoryDialog(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create category");
    }
  };

  useEffect(() => {
    fetchTransactions(true, 0, searchQuery);
    fetchCategories();
    fetchLastSyncTime();
    fetchSummary();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    fetchTransactions(true, 0, searchQuery);
  }, [categoryFilter]);
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(0);
        fetchTransactions(true, 0, searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Rental Revenue': return 'bg-emerald-100 text-emerald-700';
      case 'Sales Revenue': return 'bg-blue-100 text-blue-700';
      case 'Swap Fee': return 'bg-purple-100 text-purple-700';
      case 'Uncategorized': return 'bg-slate-100 text-slate-600';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'check': return <Receipt className="w-4 h-4" />;
      case 'bank_transfer': return <Building className="w-4 h-4" />;
      case 'cash': return <Banknote className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500">Total Revenue</div>
            <div className="text-lg font-bold text-slate-900">{formatCurrency(summary.total_revenue)}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500">This Month</div>
            <div className="text-lg font-bold text-emerald-600">{formatCurrency(summary.this_month_revenue)}</div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500">Rental Revenue</div>
            <div className="text-lg font-bold text-blue-600">
              {formatCurrency(summary.by_category?.find(c => c.category === 'Rental Revenue')?.total || 0)}
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500">Total Transactions</div>
            <div className="text-lg font-bold text-slate-700">{summary.total_count}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Transactions ({transactions.length})
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search name or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8 pr-3 py-1.5 h-9 w-[200px] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[150px] h-9 text-sm">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={() => setShowCategoryDialog(true)} className="h-9">
            <Plus className="w-4 h-4 mr-1" />
            Category
          </Button>
          
          <Button variant="default" size="sm" onClick={() => setShowCreateDialog(true)} className="h-9">
            <Plus className="w-4 h-4 mr-1" />
            Manual Entry
          </Button>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={syncing} className="h-9">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Sync Stripe
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => syncStripeTransactions(false)}>
                  Sync New Only
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => syncStripeTransactions(true)}>
                  Full Sync (All Time)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {lastSyncTime && (
              <span className="hidden md:inline text-xs text-slate-500">
                Last: {formatToEST(lastSyncTime)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Transactions List - Server-side filtering */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-12 bg-white border rounded-lg">
          <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">{searchInput ? 'No matching transactions' : 'No transactions found'}</p>
          <p className="text-sm text-slate-400 mt-1">{searchInput ? 'Try a different search term' : 'Sync from Stripe or add a manual entry'}</p>
        </div>
      ) : (
        <>
          {/* Results count */}
          <div className="text-sm text-slate-500 mb-2">
            Showing {transactions.length} of {totalCount} transactions
            {searchInput && ` matching "${searchInput}"`}
          </div>
          
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-3">
            {transactions.map(tx => (
              <div key={tx.id} className={`bg-white border rounded-lg p-3 ${tx.status && tx.status !== 'succeeded' ? 'border-red-200 bg-red-50/30' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className={`text-lg font-bold ${tx.status && tx.status !== 'succeeded' ? 'text-red-400 line-through' : 'text-slate-900'}`}>
                      {formatCurrency(tx.amount)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {tx.transaction_date ? formatToEST(tx.transaction_date) : 'Unknown date'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {tx.status && tx.status !== 'succeeded' && (
                      <Badge variant="destructive" className="text-xs">Failed</Badge>
                    )}
                    {tx.is_manual && <Badge variant="outline" className="text-xs">Manual</Badge>}
                    {getPaymentMethodIcon(tx.payment_method)}
                  </div>
                </div>
                <p className="text-sm text-slate-700 mb-2">{tx.description || 'No description'}</p>
                {tx.customer_name && (
                  <p className="text-xs text-slate-500 mb-2">{tx.customer_name}</p>
                )}
                <div className="flex items-center justify-between">
                  <Select
                    value={tx.category}
                    onValueChange={(val) => updateTransactionCategory(tx.id, val)}
                  >
                    <SelectTrigger className="w-[140px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tx.is_manual && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTransaction(tx.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Date</TableHead>
                  <TableHead className="w-[100px]">Amount</TableHead>
                  <TableHead className="w-[70px]">Status</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="w-[100px]">Method</TableHead>
                  <TableHead className="w-[150px]">Category</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map(tx => (
                  <TableRow key={tx.id} className={tx.status && tx.status !== 'succeeded' ? 'bg-red-50/50' : ''}>
                    <TableCell className="text-xs text-slate-500">
                      {tx.transaction_date ? formatToEST(tx.transaction_date) : '-'}
                    </TableCell>
                    <TableCell className={`font-mono font-medium ${tx.status && tx.status !== 'succeeded' ? 'text-red-400 line-through' : ''}`}>
                      {formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell>
                      {tx.status && tx.status !== 'succeeded' ? (
                        <Badge variant="destructive" className="text-[10px]">Failed</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">Paid</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {tx.is_manual && <Badge variant="outline" className="text-[10px]">Manual</Badge>}
                        <span className="text-sm truncate max-w-[200px]">{tx.description || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {tx.customer_name || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-500">
                        {getPaymentMethodIcon(tx.payment_method)}
                        <span className="text-xs capitalize">{tx.payment_method || 'card'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={tx.category}
                        onValueChange={(val) => updateTransactionCategory(tx.id, val)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {tx.is_manual && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteTransaction(tx.id)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4 px-2">
            <div className="text-sm text-slate-500">
              Showing {currentPage * PAGE_SIZE + 1} - {Math.min((currentPage + 1) * PAGE_SIZE, totalCount)} of {totalCount} transactions
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTransactions(true, currentPage - 1, searchQuery)}
                disabled={currentPage === 0 || loading}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {currentPage + 1} of {Math.ceil(totalCount / PAGE_SIZE) || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTransactions(true, currentPage + 1, searchQuery)}
                disabled={!hasMore || loading}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Create Manual Transaction Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Manual Transaction</DialogTitle>
            <DialogDescription>
              Record a payment received via check, cash, or bank transfer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={manualTx.amount}
                  onChange={(e) => setManualTx({ ...manualTx, amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={manualTx.payment_method}
                  onValueChange={(val) => setManualTx({ ...manualTx, payment_method: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="e.g., Monthly rental payment"
                value={manualTx.description}
                onChange={(e) => setManualTx({ ...manualTx, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={manualTx.category}
                onValueChange={(val) => setManualTx({ ...manualTx, category: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer Name</Label>
                <Input
                  placeholder="Optional"
                  value={manualTx.customer_name}
                  onChange={(e) => setManualTx({ ...manualTx, customer_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={manualTx.transaction_date}
                  onChange={(e) => setManualTx({ ...manualTx, transaction_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional notes..."
                value={manualTx.notes}
                onChange={(e) => setManualTx({ ...manualTx, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createManualTransaction}>Create Transaction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Custom Category</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Category Name</Label>
            <Input
              placeholder="e.g., Equipment Sales"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createCategory()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={createCategory}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Subscription Calendar Tab Component

export default TransactionsTab;
